import { Palette, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { THEMES } from "@/lib/refclick-utils";
import SparkleBurst, { useSparkleTrigger } from "@/components/RefClick/SparkleBurst";

/**
 * Small palette dropdown in the header.
 * Clicking a theme swaps the CSS variables via `data-theme` on <html>.
 */
export default function ThemePicker({ value, onChange, compact = false }) {
  const sparkle = useSparkleTrigger();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="theme-picker"
          onPointerEnter={sparkle.onEnter}
          onPointerDown={sparkle.onPress}
          className={
            "relative overflow-visible " +
            (compact
              ? "focus-accent flex h-8 w-8 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-white/[0.06] hover:text-accent"
              : "focus-accent flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-text-muted transition-colors hover:bg-white/10 hover:text-text-main")
          }
          aria-label="Change theme"
        >
          <Palette className="h-4 w-4" />
          <SparkleBurst trigger={sparkle.count} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52 border-white/10 bg-[#141416]/95 text-text-main backdrop-blur-xl"
      >
        <DropdownMenuLabel className="font-display text-xs uppercase tracking-widest text-text-muted">
          Theme
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/5" />
        {THEMES.map((t) => (
          <DropdownMenuItem
            key={t.id}
            data-testid={`theme-option-${t.id}`}
            onClick={() => onChange(t.id)}
            className="flex cursor-pointer items-center gap-3 focus:bg-white/5 focus:text-text-main"
          >
            <span
              aria-hidden
              className="h-4 w-4 rounded-full ring-1 ring-white/10"
              style={{
                background: t.swatch,
                boxShadow: `0 0 12px ${t.swatch}80`,
              }}
            />
            <span className="flex-1">{t.label}</span>
            {value === t.id && <Check className="h-4 w-4 text-accent" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
